import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { unwrapErrorCause } from '../utils/resource-error.utils';

@Injectable({
  providedIn: 'root',
})
export class HandleErrorsService {
  private readonly snackbar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  handleErrors(err: unknown, context?: string): void {
    const statusCode = this.resolveStatusCode(err);

    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (true) {
      case statusCode >= 500 && statusCode <= 511:
        void this.openTranslatedMessage('server-error');
        break;
      case statusCode === 404:
        void this.openTranslatedMessage('page-not-found');
        break;
      case statusCode === 401:
        if (context === 'login') {
          void this.openTranslatedMessage('incorrect-email-password');
        } else {
          void this.openTranslatedMessage('unauthorized-access');
        }
        break;
      case statusCode === 403:
        void this.openTranslatedMessage('access-denied');
        break;
      case statusCode === 406:
        void this.openTranslatedMessage('incomplete-request');
        break;
      default:
        void this.openTranslatedMessage('client-error', String(statusCode));
        break;
    }
  }

  private async openTranslatedMessage(key: string, suffix = ''): Promise<void> {
    const translations = await firstValueFrom(this.translate.get([key, 'ok']));
    const translatedMessage = translations[key];
    const message = suffix ? `${translatedMessage} ${suffix}` : translatedMessage;

    this.snackbar.open(message, translations['ok'], { duration: 7000 });
  }

  private resolveStatusCode(err: unknown): number {
    const resolvedError = unwrapErrorCause(err);

    if (typeof resolvedError !== 'object' || resolvedError === null) {
      return 500;
    }

    if ('statusCode' in resolvedError && typeof resolvedError.statusCode === 'number') {
      return resolvedError.statusCode;
    }

    if ('status' in resolvedError && typeof resolvedError.status === 'number') {
      return resolvedError.status;
    }

    return 500;
  }
}
